import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, MapPin } from 'lucide-react';

import PublicLayout from '../../components/public/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { business, cafePartners, serviceArea } from '../../content/siteContent.mjs';

const WhereToFindUs = () => (
  <PublicLayout testId="public-where-to-find-us">
    <section className="pt-16 pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto text-center">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">
          Buy a single donut
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Where to find MAD Donuts today
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed">
          We are a wholesale kitchen in {business.baseTown} with no walk-in counter — our donuts are delivered
          fresh each morning to these independent cafés, roasters and hospitality partners. Flavours vary by
          site and sell out, so call ahead for a big order.
        </p>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
          {cafePartners.length} places stocking our donuts
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partner-list">
          {cafePartners.map((partner) => (
            <Card key={partner.name} className="p-6 hover:shadow-xl transition-shadow duration-300">
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start justify-between gap-4"
                data-testid={`partner-${partner.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-amber-600 transition-colors">
                    {partner.name}
                  </h3>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                    <MapPin size={14} className="text-amber-600" />
                    {partner.location}
                  </p>
                </div>
                <ExternalLink
                  size={18}
                  className="mt-1 flex-shrink-0 text-gray-400 group-hover:text-amber-600 transition-colors"
                />
              </a>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Towns on our delivery routes</h2>
        <p className="text-lg text-gray-600 mb-10">
          Morning wholesale delivery across {business.region} and {business.nearby}.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {serviceArea.map((town) => (
            <span
              key={town}
              className="rounded-full border border-amber-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              {town}
            </span>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">Want your café on this list?</h2>
        <p className="text-lg text-gray-300 mb-9">
          Send your town and rough daily volume — we will tell you what we can supply and which morning we can
          reach you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-amber-500 text-gray-900 hover:bg-amber-400 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry" data-testid="partners-wholesale-cta">
              Start a wholesale inquiry
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
          >
            <Link to="/catering#quote">Order for an event instead</Link>
          </Button>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export default WhereToFindUs;
